import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { UsersIcon, PlayIcon, PuzzlePieceIcon, TrophyIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { LeagueDetail, Player, RatingHistory, PlayOutput } from '../generated-api';
import { DefaultService } from '../generated-api';
import { getRatingHistories } from '../services/ratingHistories';
import PlayerRatingsChart from '../components/common/PlayerRatingsChart';
import PlayList from '../components/common/PlayList';
import PlayFilterWidget from '../components/common/PlayFilterWidget';
import StatCardsCarousel from '../components/common/StatCardsCarousel';
import ActionButtons from '../components/common/ActionButtons';
import PageHeader from '../components/common/PageHeader';
import FilterButton from '../components/common/FilterButton';
import SearchButton from '../components/common/SearchButton';
import CompactRatingDisplay from '../components/common/CompactRatingDisplay';
import Tooltip from '../components/common/Tooltip';
import { useData } from '../contexts/DataContext';
import { resolveLeagueIcon } from '../utils/leagueIcon';
import { trackAction } from '../services/telemetry';

const LeagueDetailPage: React.FC = () => {
  const { id: leagueId } = useParams<{ id: string }>();

  useEffect(() => {
    if (leagueId) trackAction('league_viewed', { league_id: leagueId });
  }, [leagueId]);
  const { games, leagues: allLeagues, fetchGames, fetchLeagues } = useData();
  const [league, setLeague] = useState<LeagueDetail | null>(null);
  const [playerHistories, setPlayerHistories] = useState<Record<string, RatingHistory>>({});
  const [plays, setPlays] = useState<PlayOutput[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [playsOffset, setPlaysOffset] = useState(0);
  const [hasMorePlays, setHasMorePlays] = useState(true);
  const navigate = useNavigate();

  // Filter states
  const [selectedGame, setSelectedGame] = useState<string>('');
  const [selectedPlayerCount, setSelectedPlayerCount] = useState<string>('');
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  // Player list filter states
  const [excludeLowConfidence, setExcludeLowConfidence] = useState(true);
  const [isPlayerFilterExpanded, setIsPlayerFilterExpanded] = useState(false);
  const [playerSearchExpanded, setPlayerSearchExpanded] = useState(false);
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');

  const fetchLeague = useCallback(async () => {
    if (!leagueId) return;
    
    try {
      setLoading(true);
      const response = await DefaultService.getLeagueLeaguesLeagueIdGet({ leagueId });
      setLeague(response);
      
      // Load rating histories for league players
      if (response.players && response.players.length > 0) {
        try {
          setPlayerHistories(await getRatingHistories(leagueId));
        } catch (err) {
          console.error('Failed to load rating histories:', err);
        }
      }
    } catch (err) {
      setError('Failed to fetch league details');
      console.error('Error fetching league:', err);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  const fetchInitialData = useCallback(async () => {
    if (!leagueId) return;

    try {
      const [playsResponse, playersResponse] = await Promise.all([
        DefaultService.getPlaysPlaysGet({
          leagueId,
          limit: 200, // Maximum allowed by API to get all plays
          offset: 0,
          sort: 'newest'
        }), // Load all plays for accurate statistics
        DefaultService.getPlayersPlayersGet(),
        fetchGames(), // Use context-provided fetch
        fetchLeagues() // Use context-provided fetch
      ]);

      setPlayers(playersResponse);
      setPlays(playsResponse); // Store ALL plays

      // Reset client-side pagination
      setPlaysOffset(10); // Show first 10 plays initially
      setHasMorePlays(playsResponse.length > 10); // Check if more than 10 plays exist
    } catch (err) {
      console.error('Error fetching initial data:', err);
    }
  }, [leagueId, fetchGames, fetchLeagues]);

  useEffect(() => {
    if (leagueId) {
      // Reset pagination state when league changes
      setPlaysOffset(0);
      setHasMorePlays(true);

      fetchLeague();
      fetchInitialData();
    }
  }, [leagueId, fetchLeague, fetchInitialData]);

  const refetchPlays = useCallback(async () => {
    if (!leagueId) return;
    try {
      const playsResponse = await DefaultService.getPlaysPlaysGet({
        leagueId,
        limit: 200,
        offset: 0,
        sort: 'newest'
      });
      setPlays(playsResponse);
    } catch (err) {
      console.error('Error refetching plays:', err);
    }
  }, [leagueId]);

  const loadMorePlays = useCallback(() => {
    if (loadingMore || !hasMorePlays) return;

    // Client-side pagination - no API call needed
    // All plays are already loaded in memory
    setLoadingMore(true);

    // Simulate brief loading for better UX
    setTimeout(() => {
      const newOffset = playsOffset + 10;
      setPlaysOffset(newOffset);
      setHasMorePlays(plays.length > newOffset);
      setLoadingMore(false);
    }, 100); // Brief delay to show loading state
  }, [loadingMore, hasMorePlays, playsOffset, plays.length]);

  const handleDeleteLeague = async () => {
    if (!league || league.isSystemLeague) return;

    const confirmed = window.confirm(`Are you sure you want to delete the league "${league.name}"? This action cannot be undone.`);
    if (!confirmed) return;

    try {
      await DefaultService.deleteLeagueLeaguesLeagueIdDelete({ leagueId: league.id });
      navigate('/leagues');
    } catch (err) {
      console.error('Error deleting league:', err);
      alert('Failed to delete league');
    }
  };

  const rankedPlayers = useMemo(() => {
    if (!league?.players) return [];
    return league.players
      .map(player => {
        const gamesPlayed = player.gamesPlayed;
        const isUncertain = (gamesPlayed < 5) || (player.sigma > 5);
        return {
          ...player,
          gamesPlayed,
          conservativeRating: player.rating - 3 * player.sigma,
          isUncertain,
        };
      })
      .filter(player => {
        // Search filter
        if (playerSearchQuery.trim()) {
          const query = playerSearchQuery.toLowerCase();
          if (!player.playerName.toLowerCase().includes(query)) {
            return false;
          }
        }
        // Filter out low confidence players if the filter is enabled
        if (excludeLowConfidence && player.isUncertain) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        // Players who have played come first, sorted by conservative rating
        if (a.gamesPlayed > 0 && b.gamesPlayed === 0) return -1;
        if (a.gamesPlayed === 0 && b.gamesPlayed > 0) return 1;
        // Both have played: sort by conservative rating (descending)
        if (a.gamesPlayed > 0 && b.gamesPlayed > 0) {
          return b.conservativeRating - a.conservativeRating;
        }
        // Neither has played: sort alphabetically by name
        return a.playerName.localeCompare(b.playerName);
      });
  }, [league?.players, excludeLowConfidence, playerSearchQuery]);

  const filteredPlays = useMemo(() => {
    return plays.filter(play => {
        // Filter by game
        if (selectedGame && play.gameId !== selectedGame) {
            return false;
        }

        // Filter by player count
        if (selectedPlayerCount) {
            const playerCount = Object.values(play.teams).flat().length;
            if (selectedPlayerCount !== playerCount.toString()) {
                return false;
            }
        }

        // Filter by players - play must include ALL selected players
        if (selectedPlayers.length > 0) {
            const playPlayerIds = Object.values(play.teams).flat();
            const hasAllSelectedPlayers = selectedPlayers.every(playerId =>
                playPlayerIds.includes(playerId)
            );
            if (!hasAllSelectedPlayers) {
                return false;
            }
        }

        return true;
    });
  }, [plays, selectedGame, selectedPlayerCount, selectedPlayers]);

  // Client-side pagination: slice plays array to show only current page
  const displayedPlays = useMemo(() => {
    return filteredPlays.slice(0, playsOffset);
  }, [filteredPlays, playsOffset]);

  // Filter player histories to match the play filter selections
  const hasActivePlayFilters = !!(selectedGame || selectedPlayerCount || selectedPlayers.length > 0);
  const filteredPlayerHistories = useMemo(() => {
    if (!hasActivePlayFilters) return playerHistories;

    const filteredPlayIds = new Set(filteredPlays.map(p => p.id));
    const filtered: Record<string, RatingHistory> = {};
    for (const playerId of Object.keys(playerHistories)) {
      const history = playerHistories[playerId];
      filtered[playerId] = {
        ...history,
        ratingProgression: history.ratingProgression.filter(entry =>
          filteredPlayIds.has(entry.playId)
        ),
      };
    }
    return filtered;
  }, [playerHistories, filteredPlays, hasActivePlayFilters]);

  const leagueStatistics = useMemo(() => {
    if (!plays || plays.length === 0) {
      return {
        numberOfPlays: 0,
        distinctGames: 0,
        mostPlayedGame: null
      };
    }

    // Calculate statistics from ALL plays (not just displayed ones)
    // This ensures accurate statistics regardless of pagination
    const gameIds = new Set(plays.map(play => play.gameId));
    const distinctGames = gameIds.size;

    // Count plays per game
    const gamePlayCounts = plays.reduce((acc, play) => {
      acc[play.gameId] = (acc[play.gameId] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Find most played game with its play count
    const mostPlayedEntry = Object.entries(gamePlayCounts)
      .sort(([, countA], [, countB]) => countB - countA)[0];

    const mostPlayedGameId = mostPlayedEntry?.[0];
    const mostPlayedCount = mostPlayedEntry?.[1] || 0;
    const mostPlayedGame = games.find(game => game.id === mostPlayedGameId);

    return {
      numberOfPlays: plays.length,
      distinctGames,
      mostPlayedGame: mostPlayedGame?.name || 'N/A',
      mostPlayedGameCount: mostPlayedCount
    };
  }, [plays, games]);


  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-body">Loading league details...</p>
        </div>
      </div>
    );
  }

  if (error || !league) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error || 'League not found'}
        </div>
        <Link to="/leagues" className="text-primary-500 hover:text-primary-700">
          ← Back to Leagues
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-0 py-4 sm:px-4 sm:py-6">
      {/* Header */}
      <PageHeader paddingTop="pt-8 sm:pt-2" className="mb-8 px-4 sm:px-0">
        <Link to="/leagues" className="text-primary-500 hover:text-primary-700 mb-4 inline-block">
          ← Back to Leagues
        </Link>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-4xl">
              {resolveLeagueIcon(league.icon, league.isGlobal ? '🌍' : '🏆')}
            </div>
            <div>
              <h1 className="text-fluid-h1 font-bold text-heading">
                {league.name}
                {league.isGlobal && (
                  <span className="ml-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                    Global League
                  </span>
                )}
              </h1>
              {league.description && (
                <p className="mt-2 text-body">{league.description}</p>
              )}
              <div className="mt-2 flex items-center text-sm text-muted space-x-4">
                <span>{league.playerIds?.length || 0} players</span>
                <span>Created {new Date(league.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
          
          {!league.isSystemLeague && (
            <div className="flex items-center gap-2">
              <Link
                data-track="league_record_play"
                to={`/plays/new?leagueId=${league.id}`}
                className="inline-flex items-center justify-center sm:justify-start px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <PlayIcon className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Record Play</span>
              </Link>
              <ActionButtons
                editLink={`/leagues/${league.id}/edit`}
                onDelete={handleDeleteLeague}
                editLabel="Edit League"
                deleteLabel="Delete League"
              />
            </div>
          )}
        </div>
      </PageHeader>

      {/* League Statistics */}
      {league.players && league.players.length > 0 && (
        <StatCardsCarousel
          title="League Statistics"
          stats={[
            {
              icon: UsersIcon,
              value: league.players.filter(p => p.gamesPlayed > 0).length,
              label: 'Active Players',
              color: 'blue',
            },
            {
              icon: PlayIcon,
              value: leagueStatistics.numberOfPlays,
              label: 'Number of Plays',
              color: 'green',
            },
            ...(!league.isGameSpecificLeague ? [{
              icon: PuzzlePieceIcon,
              value: leagueStatistics.distinctGames,
              label: 'Distinct Games',
              color: 'purple' as const,
            },
            {
              icon: TrophyIcon,
              value: (leagueStatistics.mostPlayedGameCount ?? 0) > 0 ? (leagueStatistics.mostPlayedGame || 'N/A') : 'N/A',
              label: 'Most Played Game',
              color: 'orange' as const,
              secondaryValue: (leagueStatistics.mostPlayedGameCount ?? 0) > 0 ? (leagueStatistics.mostPlayedGameCount ?? 0).toString() : undefined,
              secondaryLabel: (leagueStatistics.mostPlayedGameCount ?? 0) > 0
                ? ((leagueStatistics.mostPlayedGameCount ?? 0) === 1 ? 'Play' : 'Plays')
                : undefined,
            }] : []),
          ]}
        />
      )}

      {/* Rating History Chart - Hidden for Unranked league */}
      {league && league.players && league.players.length > 0 && league.name !== 'Unranked' && (
        <PlayerRatingsChart
          players={league.players.filter(p => {
            if (p.gamesPlayed === 0) return false;
            if (excludeLowConfidence && (p.gamesPlayed < 5 || p.sigma > 5)) return false;
            return true;
          }).map(p => ({
            id: p.playerId,
            name: p.playerName,
            imageUrl: p.playerImageUrl,
            rating: p.rating,
            createdAt: ''
          } as Player))}
          playerHistories={filteredPlayerHistories}
          title={`Rating History - ${league.name}`}
          emptyMessage="No rating history available for league players yet."
          showConservativeToggle={!league.isGameSpecificLeague}
          className="mb-8"
        />
      )}

      {/* Player Rankings - Hidden for Unranked league */}
      {league.name !== 'Unranked' && (
        <div className="bg-surface sm:rounded-xl sm:shadow-md overflow-hidden">
        <div className="bg-surface-base px-6 py-4 border-b border-edge">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-fluid-h2 font-semibold text-heading">League Members</h2>
              {playerSearchQuery && (
                <p className="text-body text-sm mt-1">
                  Showing {rankedPlayers.length} of {league?.players?.length || 0} members
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <SearchButton
                isExpanded={playerSearchExpanded}
                onToggle={() => setPlayerSearchExpanded(!playerSearchExpanded)}
                value={playerSearchQuery}
                onChange={setPlayerSearchQuery}
                placeholder="Search members..."
              />
              <FilterButton
                onClick={() => setIsPlayerFilterExpanded(!isPlayerFilterExpanded)}
                isExpanded={isPlayerFilterExpanded}
                hasActiveFilters={excludeLowConfidence}
                showOnAllScreens
              />
            </div>
          </div>
        </div>

        {/* Player Filter Panel */}
        {isPlayerFilterExpanded && (
          <div className="bg-surface-base px-6 py-4 border-b border-edge">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={excludeLowConfidence}
                onChange={(e) => setExcludeLowConfidence(e.target.checked)}
                className="h-4 w-4 text-primary-600 border-edge-input rounded focus:ring-primary-500"
                data-testid="exclude-low-confidence-checkbox"
              />
              <span className="ml-2 text-sm text-body">Exclude low confidence ratings</span>
            </label>
            <p className="text-xs text-muted mt-1 ml-6">
              Hide players with fewer than 5 games or high uncertainty (σ &gt; 5)
            </p>
          </div>
        )}

        {!rankedPlayers || rankedPlayers.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-disabled mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            {league?.players && league.players.length > 0 ? (
              <>
                <h3 className="text-lg font-medium text-heading mb-2">No players match the current filters</h3>
                <p className="text-muted">Try adjusting your search or filter criteria.</p>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium text-heading mb-2">No players in this league yet</h3>
                <p className="text-muted">Add players to this league to see them listed here.</p>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-edge">
              <thead className="bg-surface-base">
                <tr>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider w-16">
                    Rank
                  </th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                    Player
                  </th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                    Rating
                  </th>
                  <th className="px-3 sm:px-4 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider w-16">
                    Games
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface divide-y divide-edge">
                {rankedPlayers.map((player, index) => {
                  const hasPlayed = player.gamesPlayed > 0;
                  // Only count ranked players for medal display
                  const rankedIndex = hasPlayed
                    ? rankedPlayers.filter((p, i) => i < index && p.gamesPlayed > 0).length
                    : -1;
                  const showUncertainIndicator = hasPlayed && player.isUncertain;
                  return (
                    <tr key={player.playerId} className={`hover:bg-surface-hover ${!hasPlayed ? 'bg-surface-subtle/50' : ''} ${showUncertainIndicator ? 'bg-surface-subtle/50 border-l-4 border-dashed border-edge' : ''}`}>
                      <td className="px-3 sm:px-4 py-3 whitespace-nowrap text-sm text-heading">
                        {hasPlayed ? (
                          <div className="flex items-center">
                            {rankedIndex === 0 && <span className="text-yellow-500 mr-1">🥇</span>}
                            {rankedIndex === 1 && <span className="text-disabled mr-1">🥈</span>}
                            {rankedIndex === 2 && <span className="text-yellow-600 mr-1">🥉</span>}
                            #{rankedIndex + 1}
                          </div>
                        ) : (
                          <span className="text-disabled">-</span>
                        )}
                      </td>
                      <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          {player.playerImageUrl && (
                            <img
                              className="h-8 w-8 rounded-full mr-3"
                              src={player.playerImageUrl}
                              alt={player.playerName}
                            />
                          )}
                          <Link
                            data-track="league_player_link"
                            to={`/players/${player.playerId}`}
                            className="text-primary-600 hover:text-primary-900 font-medium font-serif"
                          >
                            {player.playerName}
                          </Link>
                          {showUncertainIndicator && (
                            <Tooltip
                              trigger={
                                <InformationCircleIcon
                                  className="h-3.5 w-3.5 text-disabled cursor-help ml-1 flex-shrink-0"
                                />
                              }
                              content={
                                <p className="leading-relaxed">
                                  Rating is uncertain: player has played fewer than 5 games or has high uncertainty (σ &gt; 5)
                                </p>
                              }
                              hover={true}
                              showCloseButton={false}
                              width="w-72"
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                        {hasPlayed ? (
                          <div className="text-sm font-bold">
                            <CompactRatingDisplay
                              rating={player.rating}
                              sigma={player.sigma}
                              isUncertain={(player.gamesPlayed < 5) || (player.sigma > 5)}
                            />
                          </div>
                        ) : (
                          <span className="text-disabled text-sm">-</span>
                        )}
                      </td>
                      <td className="px-3 sm:px-4 py-3 whitespace-nowrap text-sm text-muted text-right">
                        {player.gamesPlayed}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </div>
      )}

      {/* League Play Sessions */}
      <div className="mt-8">
        <div className="mb-4 px-4 sm:px-0">
          <div className="flex items-center justify-between">
            <h2 className="text-fluid-h2 font-semibold text-heading">Recent Play Sessions</h2>
            <FilterButton
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
              isExpanded={isFilterExpanded}
              hasActiveFilters={!!(selectedGame || selectedPlayerCount || selectedPlayers.length > 0)}
            />
          </div>
          <p className="text-body text-sm mt-1">
            Play sessions recorded in this league
          </p>
        </div>
        <PlayFilterWidget
            games={games}
            players={players}
            plays={plays}
            selectedGame={selectedGame}
            selectedPlayerCount={selectedPlayerCount}
            selectedPlayers={selectedPlayers}
            onSelectedGameChange={setSelectedGame}
            onSelectedPlayerCountChange={setSelectedPlayerCount}
            onSelectedPlayersChange={setSelectedPlayers}
            onClearFilters={() => {
                setSelectedGame('');
                setSelectedPlayerCount('');
                setSelectedPlayers([]);
            }}
            filteredPlaysCount={filteredPlays.length}
            isExpanded={isFilterExpanded}
            onToggleExpanded={() => setIsFilterExpanded(!isFilterExpanded)}
            renderFilterButton={() => null}
        />
        <PlayList
          plays={displayedPlays}
          players={players}
          games={games}
          leagues={allLeagues}
          showActions={false}
          showPlayerDetails={true}
          showFullDetails={true}
          showPlayImages={false}
          onReactionChange={refetchPlays}
        />

        {/* Show More Button */}
        {hasMorePlays && (
          <div className="mt-6 text-center">
            <button
              onClick={loadMorePlays}
              disabled={loadingMore}
              className="bg-primary-500 hover:bg-primary-700 disabled:bg-primary-300 text-white font-medium py-2 px-6 rounded transition-colors inline-flex items-center space-x-2"
            >
              {loadingMore ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Loading...</span>
                </>
              ) : (
                <span>Show More Plays</span>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeagueDetailPage;